import Decimal from 'decimal.js';
import { isBoolTrue, createErrorObject } from '~/components/ImportWizard/ImportWizard.utils';
import { decimalFromStr } from '~/infra/utils';
import { DiscountModel, serviceModel, serviceTemplateModel } from '~/models';
import { ServiceCostType } from '~/models/service-cost';
import { ServiceTemplateBundlePricingTypeEnum } from '~/models/service-template-bundle';
export const importServicesValidationTextsPath = 'pages.importServices.validation';
export const pricingOptions = [
    'fixed',
    'hourly',
    'quantity',
    'range'
];
export const discountTypeOptions = [
    'flat',
    'percent'
];
export const billingOccurrenceOptions = [
    'one time',
    'in stages',
    'weekly',
    'biweekly',
    'monthly',
    'quarterly',
    'yearly'
];
export const bundleRelatedColumns = [
    'bundleDescription',
    'bundleBillingOccurrence',
    'bundlePricing',
    'bundleUnitName',
    'bundleUnitCap'
];
export const bundleColumnsToValidate = [
    ...bundleRelatedColumns,
    'automaticBilling',
    'discount',
    'discountType'
];
export const agreementBundleColumnsToValidate = [
    ...bundleColumnsToValidate,
    'billingDay',
    'optionalService',
    'issueInvoiceOnAcceptance',
    'startPaused',
    'priceIncreaseRate',
    'priceIncreaseStartsOn',
    'billedUpfront'
];
export const forbiddenServiceColumnsInBundleRows = [
    'service description',
    'billing occurrence',
    'pricing',
    'rate',
    'unit name',
    'unit cap',
    'discount',
    'discount type'
];
const buildPossibleValuesErrorMessage = (fieldName, values, options, texts)=>{
    const supportedValues = values.filter((o)=>{
        var _options_excludeValues;
        return !(options === null || options === void 0 ? void 0 : (_options_excludeValues = options.excludeValues) === null || _options_excludeValues === void 0 ? void 0 : _options_excludeValues.includes(o));
    });
    const last = supportedValues.pop();
    const lastOption = String(last);
    if (supportedValues.length > 0) {
        const renderedOptions = supportedValues.map((o)=>"<i>".concat(o, "</i>")).join(', ');
        return texts.invalidEntryPleaseSelectMany({
            fieldName,
            options: renderedOptions,
            lastOption
        });
    }
    return texts.invalidEntryPleaseSelectOne({
        fieldName,
        lastOption
    });
};
export const makeImportServicesErrorMessages = (importServicesValidationTexts, serviceValidationTexts)=>({
        incompatiblePriceAndPricingMsg: importServicesValidationTexts.incompatiblePriceAndPricingMsg(),
        recurrencePeriodYearlyError: importServicesValidationTexts.recurrencePeriodYearlyError(),
        fixedPriceAutomaticServiceError: importServicesValidationTexts.fixedPriceAutomaticServiceError(),
        priceError: importServicesValidationTexts.priceError(),
        rangePriceError: importServicesValidationTexts.rangePriceError(),
        positiveError: importServicesValidationTexts.positiveError(),
        nonUniqueServiceNameError: importServicesValidationTexts.nonUniqueServiceNameError(),
        nonUniqueBundleNameError: importServicesValidationTexts.nonUniqueBundleNameError(),
        minPriceExceedingError: importServicesValidationTexts.minPriceExceedingError(),
        percentDiscountLargerThan100: importServicesValidationTexts.percentDiscountLargerThan100(),
        flatDiscountGreaterThanPrice: importServicesValidationTexts.flatDiscountGreaterThanPrice(),
        bundleNameIsRequired: importServicesValidationTexts.bundleNameIsRequired(),
        bundleBillingOccurrenceIsRequired: importServicesValidationTexts.bundleBillingOccurrenceIsRequired(),
        bundlePricingIsRequired: importServicesValidationTexts.bundlePricingIsRequired(),
        serviceFieldShouldNotBeSet: importServicesValidationTexts.serviceFieldShouldNotBeSet({
            forbiddenColumns: forbiddenServiceColumnsInBundleRows.join(', ')
        }),
        getBundleUniqueServicesError: (bundleName, serviceName)=>importServicesValidationTexts.bundleUniqueServicesError({
                bundleName,
                serviceName
            }),
        getBundleMustHave2ServicesError: (bundleName)=>importServicesValidationTexts.bundleMustHave2ServicesError({
                bundleName
            }),
        getBundleServiceNotFoundError: (serviceName)=>importServicesValidationTexts.bundleServiceNotFoundError({
                serviceName
            }),
        getEmptyStringError: (fieldName)=>importServicesValidationTexts.emptyStringError({
                fieldName
            }),
        getInvalidBooleanError: (_fieldName)=>importServicesValidationTexts.invalidBooleanError(),
        getPricingError: function getPricingError() {
            let options = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {
                excludeValues: []
            };
            return buildPossibleValuesErrorMessage(importServicesValidationTexts.pricingFieldName(), pricingOptions, options, serviceValidationTexts);
        },
        getBillingOccurrenceError: function getBillingOccurrenceError() {
            let options = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {
                excludeValues: []
            };
            return buildPossibleValuesErrorMessage(importServicesValidationTexts.billingOccurrenceFieldName(), billingOccurrenceOptions, options, serviceValidationTexts);
        },
        getDiscountTypeError: function getDiscountTypeError() {
            let options = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {
                excludeValues: []
            };
            return buildPossibleValuesErrorMessage(importServicesValidationTexts.discountTypeFieldName(), discountTypeOptions, options, serviceValidationTexts);
        },
        getBundleColumnsConsistencyError: (bundleName, inconsistentColumns)=>importServicesValidationTexts.bundleColumnsConsistencyError({
                bundleName,
                inconsistentColumns: inconsistentColumns.join(', ')
            })
    });
export const isRowOfBundle = (rowObject)=>!!(rowObject.bundleName || bundleRelatedColumns.some((column)=>rowObject[column]));
export const getPricingValidations = function getPricingValidations(importServicesErrorMessages) {
    let options = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {
        excludeValues: []
    };
    return {
        validate: (pricing, param)=>{
            let { rowObject } = param;
            if ((options === null || options === void 0 ? void 0 : options.isOptional) && !pricing) return true;
            const normalized = pricing.toLowerCase();
            const validPricing = pricingOptions.includes(normalized) && !options.excludeValues.includes(normalized);
            if (!validPricing) return {
                errorType: 'pricingError'
            };
            const isNonFixedPrice = normalized !== 'fixed';
            const isAutomatic = options.actLikeManualIfBundledService && isRowOfBundle(rowObject) ? false : isBoolTrue(rowObject.automaticBilling);
            if (isNonFixedPrice && isAutomatic) return {
                errorType: 'nonFixedAutomatic'
            };
            return true;
        },
        validateError: (headerName, rowNumber, errorType)=>{
            const errorMsg = errorType === 'pricingError' ? importServicesErrorMessages.getPricingError(options) : errorType === 'nonFixedAutomatic' ? importServicesErrorMessages.fixedPriceAutomaticServiceError : '';
            if (!errorMsg) return;
            return createErrorObject(headerName, rowNumber, errorMsg);
        }
    };
};
export const getBillingOccurrenceValidations = function getBillingOccurrenceValidations(importServicesErrorMessages) {
    let options = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {
        excludeValues: []
    };
    return {
        validate: (recurrencePeriod, param)=>{
            let { rowObject } = param;
            if ((options === null || options === void 0 ? void 0 : options.isOptional) && !recurrencePeriod) return true;
            const { automaticBilling } = rowObject;
            const isAutomatic = options.actLikeManualIfBundledService && isRowOfBundle(rowObject) ? false : isBoolTrue(automaticBilling);
            const normalizedRecurrencePeriod = recurrencePeriod === null || recurrencePeriod === void 0 ? void 0 : recurrencePeriod.toLowerCase();
            const isValidRecurrencePeriod = billingOccurrenceOptions.includes(normalizedRecurrencePeriod) && !options.excludeValues.includes(normalizedRecurrencePeriod);
            if (!isValidRecurrencePeriod) return {
                valid: false,
                errorType: 'invalidValue'
            };
            const isYearly = normalizedRecurrencePeriod === 'yearly';
            return {
                valid: !(isYearly && isAutomatic),
                errorType: 'yearlyAndAutomatic'
            };
        },
        validateError: (headerName, rowNumber, errorType)=>{
            switch(errorType){
                case 'invalidValue':
                    return createErrorObject(headerName, rowNumber, importServicesErrorMessages.getBillingOccurrenceError(options));
                case 'yearlyAndAutomatic':
                    return createErrorObject(headerName, rowNumber, importServicesErrorMessages.recurrencePeriodYearlyError);
            }
        }
    };
};
export const getUnitCapValidations = function getUnitCapValidations(importServicesErrorMessages) {
    let _options = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {
        excludeValues: []
    };
    return {
        validate: (unitCap)=>{
            if ((unitCap === null || unitCap === void 0 ? void 0 : unitCap.length) === 0) return true;
            const decimal = decimalFromStr(unitCap);
            if (!decimal) return false;
            const isPositiveNumber = decimal.isPositive();
            const isNonZero = !decimal.isZero();
            return isPositiveNumber && isNonZero;
        },
        validateError: (headerName, rowNumber)=>createErrorObject(headerName, rowNumber, importServicesErrorMessages.positiveError)
    };
};
export const getServiceNameValidations = (param)=>{
    let { isBundleName, texts } = param;
    const label = isBundleName ? texts.bundleNameLabel() : texts.serviceNameLabel();
    return {
        validate: (serviceName)=>{
            if (isBundleName && !serviceName) return {
                valid: true
            };
            const error = serviceModel.validateName(serviceName, {
                isBundleName,
                texts,
                label
            });
            return error ? {
                valid: false,
                errorType: error
            } : {
                valid: true
            };
        },
        validateError: (headerName, rowNumber, errorType)=>{
            if (errorType) {
                return createErrorObject(headerName, rowNumber, errorType);
            }
        }
    };
};
export const getBundlePricingType = (row)=>{
    if ((row.bundlePricing || '').toLowerCase() === 'fixed') {
        return {
            type: ServiceTemplateBundlePricingTypeEnum.Fixed
        };
    } else {
        return {
            type: ServiceTemplateBundlePricingTypeEnum.Variable,
            variable: {
                cap: row.bundleUnitCap ? decimalFromStr(row.bundleUnitCap) : null,
                name: row.bundleUnitName || null
            }
        };
    }
};
export const getServiceCost = function getServiceCost(row) {
    let { ignoreServiceDiscount, useBundleFields, customRate } = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {
        ignoreServiceDiscount: false,
        useBundleFields: false
    };
    var _ref, _decimalFromStr;
    var _decimalFromStr1;
    const [pricingField, unitCapField, unitNameField] = useBundleFields ? [
        'bundlePricing',
        'bundleUnitCap',
        'bundleUnitName'
    ] : [
        'pricing',
        'unitCap',
        'unitName'
    ];
    const rate = (_ref = customRate !== null && customRate !== void 0 ? customRate : row.rate) !== null && _ref !== void 0 ? _ref : '';
    const pricing = (row[pricingField] || '').toLowerCase();
    const pricingType = pricing === 'fixed' ? ServiceCostType.Fixed : pricing === 'range' ? ServiceCostType.Range : ServiceCostType.Variable;
    const discountAmount = row.discount && !Number.isNaN(+row.discount) ? +row.discount : null;
    const hasMinPrice = pricingType === 'range' && rate.includes('-');
    const minPrice = hasMinPrice && ((_decimalFromStr1 = decimalFromStr(rate.split('-')[0])) === null || _decimalFromStr1 === void 0 ? void 0 : _decimalFromStr1.toDP(2).toNumber()) || null;
    const price = ((_decimalFromStr = decimalFromStr(hasMinPrice ? rate.split('-')[1] : rate)) !== null && _decimalFromStr !== void 0 ? _decimalFromStr : new Decimal(0)).toDP(2);
    const serviceDiscount = !ignoreServiceDiscount && discountAmount != null ? (row.discountType || '').toLowerCase() === 'percent' ? DiscountModel.makePercent(discountAmount) : DiscountModel.makeValue(discountAmount) : null;
    switch(pricingType){
        case ServiceCostType.Fixed:
            return serviceTemplateModel.makeFixedCost(price, {
                discount: serviceDiscount
            });
        case ServiceCostType.Variable:
            {
                const decimalUnitCap = decimalFromStr(row[unitCapField]);
                return serviceTemplateModel.makeVariableCost(price, {
                    unitCap: decimalUnitCap || 0,
                    unitName: row[unitNameField] || null,
                    discount: serviceDiscount
                });
            }
        case ServiceCostType.Range:
            return serviceTemplateModel.makeRangeCost(price, {
                minPrice,
                discount: serviceDiscount
            });
    }
};
